import { useEffect, useState } from "react";
import "../../assets/css/Expenses/ExpensesDrawer.css";
import { MdClose, MdCalendarToday, MdOutlineNotes } from "react-icons/md";
import { FaMoneyBillWave, FaBuilding, FaUser, FaTag, FaUserCircle } from "react-icons/fa";

export default function ExpenseDrawer({
  expense,
  isOpen,
  onClose,
}) {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (!expense) return;

    setFormData({
      expense_type: expense.expense_type || "",
      title: expense.lead ? expense.lead.title : expense.deal ? expense.deal.title : "Expense Details",
      amount: expense.amount || 0,
      remarks: expense.remarks || "",
      expense_date: expense.expense_date || "",
    });
  }, [expense]);

  if (!isOpen || !expense) return null;

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div
        className="expense-drawer"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="drawer-header">
          <div className="drawer-top">
            <div className="expense-header-info">
              <div className="expense-avatar">
                <FaMoneyBillWave />
              </div>

              <div className="expense-info-head">
                <span className="expense-type-chip">
                  <FaTag size={10} /> {formData.expense_type || "General Expense"}
                </span>
                <h2>{formData.title}</h2>
              </div>
            </div>

            <button
              className="close-btn"
              onClick={onClose}
              title="Close Drawer"
            >
              <MdClose size={20} />
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="drawer-section">
          <div className="drawer-content">

            {/* KEY METRICS SUMMARY BAR */}
            <div className="expense-hero-banner">
              <div className="hero-stat-box primary">
                <span className="hero-stat-label">Total Expense</span>
                <span className="hero-stat-value">₹{Number(formData.amount).toLocaleString()}</span>
              </div>
              <div className="hero-stat-box">
                <span className="hero-stat-label">Category</span>
                <span className="hero-stat-value text-amber">{formData.expense_type || "N/A"}</span>
              </div>
              <div className="hero-stat-box">
                <span className="hero-stat-label">Expense Date</span>
                <span className="hero-stat-value text-slate">
                  {formData.expense_date
                    ? new Date(formData.expense_date).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })
                    : "-"}
                </span>
              </div>
            </div>

            {/* DETAILED INFO GRID */}
            <div className="expense-detail-grid">
              <div className="expense-card-box">
                <label><FaTag /> Expense Category</label>
                <p>{formData.expense_type || "-"}</p>
              </div>

              <div className="expense-card-box">
                <label><MdCalendarToday /> Expense Date</label>
                <p>
                  {formData.expense_date
                    ? new Date(formData.expense_date).toLocaleDateString('en-IN', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })
                    : "-"}
                </p>
              </div>

              <div className="expense-card-box">
                <label><FaUserCircle /> Created By</label>
                <p>{expense.created_by?.name || expense.created_by || "-"}</p>
              </div>

              <div className="expense-card-box">
                <label><FaMoneyBillWave /> Recorded Amount</label>
                <p className="highlight-amount">₹{Number(formData.amount).toLocaleString()}</p>
              </div>

              <div className="expense-card-box full remarks-box">
                <label><MdOutlineNotes /> Remarks & Notes</label>
                <p className="remarks-text">{formData.remarks || "No additional remarks provided."}</p>
              </div>
            </div>

            {/* ORGANIZATION SECTION */}
            <div className="info-section">
              <div className="section-title">
                <FaBuilding />
                <span>Associated Organization</span>
              </div>

              {expense.organization ? (
                <div className="crm-card">
                  <div className="crm-card-icon building">
                    <FaBuilding />
                  </div>

                  <div className="crm-card-content">
                    <h4>{expense.organization.name}</h4>
                    <p className="website-link">
                      {expense.organization.website || "No website specified"}
                    </p>
                    <div className="crm-meta">
                      {expense.organization.country && (
                        <span className="meta-badge">{expense.organization.country}</span>
                      )}
                      {expense.organization.state && (
                        <span className="meta-badge">{expense.organization.state}</span>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-card">
                  No Organization Linked
                </div>
              )}
            </div>

            {/* LINKED LEAD OR DEAL */}
            <div className="info-section">
              <div className="section-title">
                {expense.deal ? <FaMoneyBillWave /> : <FaUser />}
                <span>Linked {expense.deal ? "Deal" : expense.lead ? "Lead" : "Entity"}</span>
              </div>

              {expense.deal ? (
                <div className="crm-card deal-card">
                  <div className="crm-card-icon deal">
                    <FaMoneyBillWave />
                  </div>

                  <div className="crm-card-content">
                    <h4>{expense.deal.title}</h4>
                    <p>Deal Value: <strong>₹{Number(expense.deal.deal_amount || 0).toLocaleString()}</strong></p>
                    <div className="crm-meta">
                      <span className="status-chip success">{expense.deal.status || "Open"}</span>
                      {expense.deal.deal_code && (
                        <span className="meta-code">{expense.deal.deal_code}</span>
                      )}
                    </div>
                  </div>
                </div>
              ) : expense.lead ? (
                <div className="crm-card lead-card">
                  <div className="crm-card-icon lead">
                    <FaUser />
                  </div>

                  <div className="crm-card-content">
                    <h4>{expense.lead.title}</h4>
                    <p>Status: <strong>{expense.lead.status}</strong></p>
                    <div className="crm-meta">
                      <span className="status-chip info">{expense.lead.lead_type || "One-Time"}</span>
                      {expense.lead.value && (
                        <span className="meta-badge">Valued at ₹{Number(expense.lead.value).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-card">
                  No Lead or Deal Linked
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}